import { ArrowLeft, Shield, Heart, MessageCircle, AlertCircle, Sparkles, CheckCircle } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';

export default function BoundaryPracticeGuide() {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-gradient-to-b from-indigo-50 to-white">
      <div className="max-w-4xl mx-auto px-4 py-8">
        <Button
          variant="ghost"
          onClick={() => navigate(-1)}
          className="mb-6 text-indigo-700 hover:text-indigo-800 hover:bg-indigo-100"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Week
        </Button>

        <div className="bg-white rounded-2xl shadow-xl p-8 mb-8">
          <div className="flex items-center gap-3 mb-6">
            <div className="p-3 bg-indigo-100 rounded-full">
              <Shield className="w-8 h-8 text-indigo-600" />
            </div>
            <div>
              <h1 className="text-3xl font-serif font-bold text-gray-900">Boundary Practice</h1>
              <p className="text-indigo-600">Week 10, Day 7 - Social Connections</p>
            </div>
          </div>

          <div className="prose prose-indigo max-w-none">
            <p className="text-lg text-gray-700 leading-relaxed mb-6">
              Healthy boundaries protect your energy, reduce resentment, and actually improve relationships. 
              Today, practice setting a boundary with kindness but firmness.
            </p>

            <div className="bg-indigo-50 rounded-xl p-6 mb-8">
              <h2 className="text-xl font-semibold text-indigo-800 mb-4 flex items-center gap-2">
                <Heart className="w-5 h-5" />
                Why Boundaries Matter
              </h2>
              <ul className="space-y-2 text-gray-700">
                <li>• Protect your time, energy, and well-being</li>
                <li>• Prevent burnout and resentment</li>
                <li>• Show others how to treat you</li>
                <li>• Allow you to give from abundance, not depletion</li>
                <li>• Build self-respect and confidence</li>
                <li>• Actually improve relationships over time</li>
              </ul>
            </div>

            <div className="bg-rose-50 rounded-xl p-6 mb-8">
              <h2 className="text-xl font-semibold text-rose-800 mb-4 flex items-center gap-2">
                <AlertCircle className="w-5 h-5" />
                Signs You Need a Boundary
              </h2>
              <ul className="space-y-2 text-gray-700">
                <li>• You feel resentful or taken advantage of</li>
                <li>• You're always exhausted after certain interactions</li>
                <li>• You say yes when you want to say no</li>
                <li>• You feel guilty for taking care of yourself</li>
                <li>• Someone's behavior consistently crosses a line</li>
                <li>• You avoid certain people or situations</li>
              </ul>
            </div>

            <h2 className="text-2xl font-serif font-bold text-gray-900 mb-4">Ways to Say No</h2>
            
            <div className="space-y-3 mb-8">
              <div className="bg-gray-50 rounded-lg p-4 border-l-4 border-indigo-400">
                <p className="text-gray-700 font-medium">"I can't commit to that right now."</p>
                <p className="text-gray-500 text-sm mt-1">Simple, clear, no over-explanation needed.</p>
              </div>
              <div className="bg-gray-50 rounded-lg p-4 border-l-4 border-indigo-400">
                <p className="text-gray-700 font-medium">"That doesn't work for me."</p>
                <p className="text-gray-500 text-sm mt-1">You don't need to justify why.</p>
              </div>
              <div className="bg-gray-50 rounded-lg p-4 border-l-4 border-indigo-400">
                <p className="text-gray-700 font-medium">"I need to check my schedule and get back to you."</p>
                <p className="text-gray-500 text-sm mt-1">Buys time when caught off guard.</p>
              </div>
              <div className="bg-gray-50 rounded-lg p-4 border-l-4 border-indigo-400">
                <p className="text-gray-700 font-medium">"I have a policy of not [doing X]."</p>
                <p className="text-gray-500 text-sm mt-1">Makes it about a rule, not personal rejection.</p>
              </div>
              <div className="bg-gray-50 rounded-lg p-4 border-l-4 border-indigo-400">
                <p className="text-gray-700 font-medium">"I'm not available for that, but I hope it goes well."</p>
                <p className="text-gray-500 text-sm mt-1">Kind but firm decline.</p>
              </div>
            </div>

            <h2 className="text-2xl font-serif font-bold text-gray-900 mb-4">The Boundary Formula</h2>
            
            <div className="bg-gradient-to-r from-indigo-100 to-purple-100 rounded-xl p-6 mb-8">
              <div className="space-y-4">
                <div className="flex items-start gap-3">
                  <div className="w-8 h-8 bg-indigo-500 text-white rounded-full flex items-center justify-center font-bold shrink-0">1</div>
                  <div>
                    <h3 className="font-semibold text-gray-900">State the behavior</h3>
                    <p className="text-gray-600">"When you [specific behavior]..."</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-8 h-8 bg-indigo-500 text-white rounded-full flex items-center justify-center font-bold shrink-0">2</div>
                  <div>
                    <h3 className="font-semibold text-gray-900">Share the impact</h3>
                    <p className="text-gray-600">"...I feel [emotion] because [reason]..."</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-8 h-8 bg-indigo-500 text-white rounded-full flex items-center justify-center font-bold shrink-0">3</div>
                  <div>
                    <h3 className="font-semibold text-gray-900">State your need</h3>
                    <p className="text-gray-600">"...I need [specific request]."</p>
                  </div>
                </div>
              </div>
              <div className="mt-4 p-4 bg-white rounded-lg">
                <p className="text-gray-700 italic">
                  Example: "When you call me after 9 PM, I feel stressed because that's my wind-down time. 
                  I need you to text before calling after 9 PM."
                </p>
              </div>
            </div>

            <div className="bg-amber-50 rounded-xl p-6 mb-8">
              <h2 className="text-xl font-semibold text-amber-800 mb-4 flex items-center gap-2">
                <MessageCircle className="w-5 h-5" />
                Remember
              </h2>
              <ul className="space-y-2 text-gray-700">
                <li>• "No" is a complete sentence</li>
                <li>• You don't need to over-explain or justify</li>
                <li>• Their reaction is not your responsibility</li>
                <li>• Boundaries aren't mean - they're self-care</li>
                <li>• Start with smaller boundaries and build up</li>
                <li>• Practice makes it easier over time</li>
              </ul>
            </div>

            <div className="bg-gradient-to-r from-green-50 to-emerald-50 rounded-xl p-6">
              <h2 className="text-xl font-semibold text-green-800 mb-4 flex items-center gap-2">
                <CheckCircle className="w-5 h-5" />
                Today's Practice
              </h2>
              <p className="text-gray-700 mb-4">
                Identify one situation where you need a boundary. It could be:
              </p>
              <ul className="space-y-2 text-gray-700 mb-4">
                <li>• Saying no to an invitation you don't want to accept</li>
                <li>• Asking someone not to interrupt you</li>
                <li>• Setting limits on work hours</li>
                <li>• Declining to discuss certain topics</li>
                <li>• Asking for space or alone time</li>
              </ul>
              <p className="text-green-700 font-medium">
                Practice saying your boundary out loud before you need to use it. 
                When the moment comes, speak with kindness but clarity.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
